import React, { useState, useRef, useEffect } from 'react';
import {
  Wrapper,
  InputMask,
  Placeholder,
  LoaderWrapper,
  Hidden,
  ErrorMessage
} from './styled';
import { Icon } from '@app/src/common/Icon';
import { SelectItemModel } from './SelectModel';
import { COLORS } from '@app/src/ui';
import { isClientEnv } from '@app/src/utils/validations';
import { Modal } from '@app/src/common/Modal';
import { SelectOptions } from './SelectOptions';

const KEY_DOWN = 40;
const KEY_UP = 38;
const ESC = 27;
const ENTER = 13;
const isClient = isClientEnv();
const BREAKPOINT = 768;
const PLACEHOLDER_INDEX = 0;

interface Props {
  initialIndex?: number;
  handleChange?: Function;
  dataList: SelectItemModel[];
  placeholder: string;
  isLoading?: boolean;
  disabled?: boolean;
  modalTitle?: string;
  optionColor?: string;
  handleChangeSelect?: Function;
  error?: boolean;
}

export const Select: React.FC<Props> = ({
  initialIndex,
  handleChange,
  dataList,
  placeholder,
  isLoading,
  disabled,
  handleChangeSelect,
  modalTitle,
  optionColor,
  error
}) => {
  const [data, setData] = useState([]);
  const [isBehaviorModal, setIsBehaviorModal] = useState(false);
  const refWrapper = useRef(null);
  const [showOptions, setShowOptions] = useState(false);
  const [selectText, setSelectText] = useState(placeholder);
  const [activeSuggestion, setActiveSuggestion] = useState(PLACEHOLDER_INDEX);
  const [displayModal, setDisplayModal] = useState(false);
  let refItems = null;
  const refInput = useRef(null);
  const refModal = useRef(null);

  useEffect(() => {
    if (isClient) {
      handleResizeWindow();
      window.addEventListener('resize', handleResizeWindow);
      return () => {
        window.removeEventListener('resize', handleResizeWindow);
      }
    }
  }, []);

  const handleResizeWindow = () => {
    if (window.innerWidth >= BREAKPOINT) {
      setIsBehaviorModal(false);
    } else {
      setDisplayModal(false);
      setIsBehaviorModal(true);
    }
  }

  useEffect(() => {
    if (data && data.length > 0) {
      if (data[activeSuggestion]) {
        setSelectText(data[activeSuggestion] && data[activeSuggestion].text);
      }
    }
  }, [data, activeSuggestion])

  useEffect(() => {
    if (dataList && dataList.length > 0) {
      const optionPlaceholder = {
        text: placeholder,
        value: '',
        data: {
          id: null,
          name: null
        }
      }
      const newData = [optionPlaceholder, ...dataList];
      setData(newData);
      if (initialIndex) {
        for (let i = 0; i < newData.length; i++) {
          if (Number(newData[i].value) === initialIndex) {
            setActiveSuggestion(i);
          }
        }
      } else {
        setActiveSuggestion(0);
      }
    }
  }, [dataList, initialIndex]);

  useEffect(() => {
    if (isClient) {
      if (showOptions) {
        document.addEventListener('click', handleCloseSuggestion);
      }
      return () => {
        document.removeEventListener('click', handleCloseSuggestion);
      }
    }
  }, [showOptions]);

  const handleClickInput = () => {
    if (isClient) {
      if (window.innerWidth >= BREAKPOINT) {
        setShowOptions(!showOptions);
      } else {
        setDisplayModal(true);
        refInput.current.blur();
      }
      if (showOptions) {
        document.addEventListener('click', handleCloseSuggestion);
      }
    }
  }

  const handleCloseSuggestion = (e: MouseEvent) => {
    const isClickOutside = !refWrapper.current.contains(e.target);
    if (isClickOutside) {
      setShowOptions(false);
    }
  };

  const handleClickOption = (index: number) => {
    setActiveSuggestion(Number(index));
    handleChange && handleChange(data[index].data);
    setShowOptions(false);
  }

  const handleChangeOption = (index: number) => {
    setActiveSuggestion(Number(index));
    handleChange && handleChange(data[index].data);
    setTimeout(() => {
      setDisplayModal(false);
    }, 100)
  }

  const handleKeyInput = (e: React.KeyboardEvent) => {
    const keyCode = e.keyCode || e.which;
    if (keyCode === KEY_DOWN) {
      if (activeSuggestion < data.length - 1) {
        setActiveSuggestion(activeSuggestion + 1);
      }
      refItems[activeSuggestion].current.focus();
    }
    if (keyCode === KEY_UP) {
      refItems[activeSuggestion].current.focus();
      if (activeSuggestion > PLACEHOLDER_INDEX) {
        setActiveSuggestion(activeSuggestion - 1);
      }
    }
    if (keyCode === ESC) {
      setShowOptions(false);
    }
  }

  const handleKeyDownList = (e: React.KeyboardEvent) => {
    const keyCode = e.keyCode || e.which;
    if (keyCode === KEY_DOWN) {
      if (activeSuggestion < data.length - 1) {
        setActiveSuggestion(activeSuggestion + 1);
      }
    }
    if (keyCode === KEY_UP) {
      if (activeSuggestion > 0) {
        setActiveSuggestion(activeSuggestion - 1);
      }
      if (activeSuggestion === 0) {
        refInput.current.focus();
        setShowOptions(false);
      }
    }
    if (keyCode === ENTER) {
      setActiveSuggestion(activeSuggestion);
      handleChange && handleChange(data[activeSuggestion].data);
      setShowOptions(false);
    }
    if (keyCode === ESC) {
      setShowOptions(false);
    }
  }

  const setPlaceholder = () => {
    return selectText === placeholder ? COLORS.GRAY_PLACEHOLDER : COLORS.BLACK;
  }

  const handleCloseModal = () => {
    setDisplayModal(false);
  }

  const titleSelected = () => {
    if (isLoading) {
      return placeholder;
    }
    return selectText || placeholder;
  }

  const handleChangeProp = (e) => {
    handleChangeSelect && handleChangeSelect(e);
    setActiveSuggestion(e.target.value);
    handleChange && handleChange(data && data[e.target.value].data);
  }

  return(
  <React.Fragment>
    <Wrapper ref={refWrapper}>
      <Hidden value={activeSuggestion} onChange={handleChangeProp}>
        {
          data.map((item, index) => (
            <option key={index} value={item.value}>{item.text}</option>
          ))
        }
      </Hidden>
      <InputMask
        ref={refInput}
        onClick={handleClickInput}
        onKeyDown={handleKeyInput}
        disabled={disabled || isLoading}
        tabIndex="0"
        error={error}
      >
        <Placeholder color={setPlaceholder()}>{titleSelected()}</Placeholder>
        {
          (isLoading) ?
          <LoaderWrapper>
              <Icon data-name='icon-circle' fill='#ccc' width={'17'} height={'17'} />
          </LoaderWrapper>
          :
          <Icon data-name="icon-expand-more" width="16" height="16" fill={COLORS.GRAY_DARK} />
        }
      </InputMask>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <SelectOptions
        isDisplay={showOptions}
        data={data}
        activeIndex={activeSuggestion}
        placeholder={placeholder}
        handleChange={handleClickOption}
        optionColor={optionColor}
        showFirstItems={7}
        handleRefItems={refs => { refItems = refs }}
        handleKeyDown={handleKeyDownList}
      />
    </Wrapper>
    {
      isBehaviorModal &&
        <Modal ref={refModal} show={displayModal} closeModal={handleCloseModal} customBody>
          <SelectOptions
            isDisplay={true}
            data={data}
            activeIndex={activeSuggestion}
            placeholder={placeholder}
            handleChange={handleChangeOption}
            modalTitle={modalTitle}
            optionColor={optionColor}
            showFirstItems={7}
          />
        </Modal>
    }
    </React.Fragment>
)};
